import { useQuery } from '@tanstack/react-query';
import {
  StatusCode,
  getApiV2DynamicConfigsName,
  type DynamicConfig as DynamicConfigInterface,
  getDynamicConfigs,
  type DynamicConfigsParams as Params,
} from '@hungryroot/rest';

const TIME_LIMIT_15_MINUTES = 15 * 60 * 1000;

export type { DynamicConfigInterface };

export enum DynamicConfigs {
  RolloverMinPointsPerOrder = 'rollover.min_points_per_order',
  CustomizeMarketingRecipesTitle3 = 'customize.marketing_recipes_title_3',
  CustomizeMarketingSkusTitle = 'customize.marketing_skus_title',
  CustomizeVolumeLimit = 'customize.volume_limit',
  CustomizeColdVolumeLimit = 'customize.cold_volume_limit',
  KidsSnacksGroceryNav = 'kids.enable_kids_grocery_nav',
  BestPrice = 'bpg.enable_bpg_grocery_nav',
  AntiInflammatory = 'anti_inflammatory.enable_anti_inflammatory_filter_nav',
  FunnelThirdPartySSODisabledUserAgents = 'funnel.third_party_sso_disabled_user_agents',
  InsufficientOrderThreshold = 'ssc.insufficient_order_threshold',
  AlgoliaClientKeyProduction = 'web.algolia_client_key.production',
  AlgoliaClientKeyStaging = 'web.algolia_client_key.staging',
  AlgoliaClientKeyQA = 'web.algolia_client_key.qa',
  // Artificial pre-request delay (ms) applied to the Algolia search request
  // for exp-231 variant 2, to isolate latency from result quality.
  AlgoliaExp231Delay = 'algolia.exp_231_delay',
  DiscoveryHomeReferralNudge = 'discovery_home.api.referral_nudge',
  HomepageBrowse = 'api.homepage.browse',
  ExperimentsEnrollingViaApi = 'web.experiments_enrolling_via_api',
  OccasionFilters = 'api.favorites.occasion_filters',
  PairingInventoryNotificationsEnabled = 'api.inventory_notifications.pairing_enabled',
  // `api.cookbooks.customer_created_enabled` is deliberately not here: it is
  // registered in `@hungryroot/feature-flags` instead, so that gating on it
  // honours the local-storage overrides the debug menu and Playwright write.
  // See `useCustomerCookbooksEnabled`.
  //
  // Exp 252 cookbook cover art. Two configs rather than a dedicated endpoint so
  // marketing can add/remove/reorder options from the admin without a release —
  // seasonal and campaign art is the stated future use.
  CookbookHeroColorOptions = 'api.cookbooks.hero_color_options',
  CookbookHeroImageOptions = 'api.cookbooks.hero_image_options',
  // Exp 195 Ingredient Dislikes common-search pills. JSON list of strings
  // (BE-8359). Edited in admin, so the client does not keep its own list.
  FoodProfileAvoidanceCommonSearchTerms = 'food_profile.avoidance.common_search_terms',
  // Backend switch for re-shuffling an order on unskip (BE-6888). Web reads it
  // to know when a cart change chained onto an unskip has to wait for that
  // shuffle (WEB-7053), so turning it off reverts both sides together.
  ShuffleOrderOnUnskip = 'api.shuffle_order_on_unskip.enabled',
}

enum REACT_QUERY_KEYS {
  DYNAMIC_CONFIGS = 'DynamicConfigs',
}

export enum DynamicConfigTypes {
  Customize = 'customize',
}

export type DynamicConfigParam = DynamicConfigs | DynamicConfigTypes;

const DYNAMIC_CONFIGS_PATH = '/api/v2/dynamic_configs/';

export function useDynamicConfigs(
  params: Params<(DynamicConfigs | DynamicConfigTypes)[]>,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['dynamic_config', params],
    queryFn: async () => {
      const resp = await getDynamicConfigs(params);

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    staleTime: 5 * 60 * 1000,
  });
}

export function getCartSaveThresholdDynamicConfig(
  dynamicConfigs?: DynamicConfigInterface<DynamicConfigs | DynamicConfigTypes>[]
): number {
  if (dynamicConfigs === undefined) {
    return 0;
  }

  const match = dynamicConfigs.find(({ name }) => {
    return name === DynamicConfigs.RolloverMinPointsPerOrder;
  });

  if (match === undefined) {
    return 0;
  }

  return parseFloat(match.value);
}

export function useDynamicConfiguration(key: string) {
  return useQuery({
    queryKey: [REACT_QUERY_KEYS.DYNAMIC_CONFIGS, DYNAMIC_CONFIGS_PATH, key],
    queryFn: async () => {
      const resp = await getApiV2DynamicConfigsName({
        name: key,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    gcTime: TIME_LIMIT_15_MINUTES,
    staleTime: TIME_LIMIT_15_MINUTES,
  });
}

export function getDynamicConfigValue(
  dynamicConfigs: DynamicConfigInterface<DynamicConfigs | DynamicConfigTypes>[],
  value: DynamicConfigs
) {
  const obj = dynamicConfigs.find((obj) => obj.name === value);

  return obj?.value;
}
